/**
 * Preset Service - Async preset management with server-first approach and localStorage fallback
 */

import type { FilterPreset, FilterGroup } from '../types';
import { STORAGE_KEYS, MAX_NESTING_DEPTH } from '../config/constants';
import { API_CONFIG, STORAGE_MODE } from '../config/api.config';
import { get, post, put, del } from './http.service';
import type { ApiResponse, StorageMode } from '../types/api.types';

// Track current storage mode
let currentStorageMode: StorageMode = STORAGE_MODE.SERVER;

/**
 * Get the current storage mode
 */
export function getStorageMode(): StorageMode {
  return currentStorageMode;
}

/**
 * Set the current storage mode
 */
function setStorageMode(mode: StorageMode): void {
  currentStorageMode = mode;
}

/**
 * Generate unique preset ID
 */
function generatePresetId(): string {
  return `preset-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// ============================================
// LocalStorage Operations (Fallback)
// ============================================

/**
 * Read presets from localStorage
 */
export function getPresetsFromLocalStorage(): FilterPreset[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.FILTER_PRESETS);
    if (!stored) return [];
    return JSON.parse(stored) as FilterPreset[];
  } catch (error) {
    console.error('Error reading presets from localStorage:', error);
    return [];
  }
}

/**
 * Save presets to localStorage
 */
function savePresetsToLocalStorage(presets: FilterPreset[]): void {
  try {
    localStorage.setItem(STORAGE_KEYS.FILTER_PRESETS, JSON.stringify(presets));
  } catch (error) {
    console.error('Error saving presets to localStorage:', error);
  }
}

/**
 * Get preset from localStorage by ID
 */
export function loadPresetFromLocalStorage(id: string): FilterGroup | null {
  const presets = getPresetsFromLocalStorage();
  const preset = presets.find(p => p.id === id);
  if (!preset) return null;
  return JSON.parse(JSON.stringify(preset.filterRoot)); // Deep clone
}

// ============================================
// Server Operations
// ============================================

/**
 * Build the full API URL
 */
function getApiUrl(endpoint: string): string {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
}

/**
 * Check if server is available
 */
export async function checkServerAvailability(): Promise<boolean> {
  try {
    const response = await get<ApiResponse<FilterPreset[]>>(getApiUrl(API_CONFIG.ENDPOINTS.PRESETS));
    return response.success;
  } catch {
    return false;
  }
}

/**
 * Get all presets from server
 */
async function getPresetsFromServer(): Promise<FilterPreset[]> {
  const response = await get<ApiResponse<FilterPreset[]>>(getApiUrl(API_CONFIG.ENDPOINTS.PRESETS));
  if (!response.success || !response.data) {
    throw new Error('Failed to fetch presets');
  }
  return response.data;
}

/**
 * Save preset to server
 */
async function savePresetToServer(name: string, filterRoot: FilterGroup): Promise<FilterPreset> {
  const response = await post<ApiResponse<FilterPreset>>(getApiUrl(API_CONFIG.ENDPOINTS.PRESETS), {
    name,
    filterRoot
  });

  if (!response.success || !response.data) {
    throw new Error('Failed to save preset');
  }

  return response.data;
}

/**
 * Rename preset on server
 */
async function renamePresetOnServer(id: string, newName: string): Promise<boolean> {
  const response = await put<ApiResponse<FilterPreset>>(getApiUrl(`${API_CONFIG.ENDPOINTS.PRESETS}/${id}`), {
    name: newName
  });

  return response.success;
}

/**
 * Delete preset from server
 */
async function deletePresetFromServer(id: string): Promise<boolean> {
  const response = await del<ApiResponse<null>>(getApiUrl(`${API_CONFIG.ENDPOINTS.PRESETS}/${id}`));
  return response.success;
}

// ============================================
// Public Async API (Server-first with fallback)
// ============================================

/**
 * Get all presets - try server first, fall back to localStorage
 */
export async function getPresets(): Promise<FilterPreset[]> {
  try {
    const serverPresets = await getPresetsFromServer();
    setStorageMode(STORAGE_MODE.SERVER);

    // Also check for local-only presets
    const localPresets = getPresetsFromLocalStorage();

    if (localPresets.length > 0) {
      // Merge: server presets take precedence, but keep local-only ones
      const serverIds = new Set(serverPresets.map(p => p.id));
      const localOnlyPresets = localPresets.filter(p => !serverIds.has(p.id));

      if (localOnlyPresets.length > 0) {
        setStorageMode(STORAGE_MODE.MIXED);
        return [...serverPresets, ...localOnlyPresets];
      }
    }

    return serverPresets;
  } catch (error) {
    console.warn('Server unavailable, using localStorage:', error);
    setStorageMode(STORAGE_MODE.LOCAL);
    return getPresetsFromLocalStorage();
  }
}

/**
 * Save a preset - try server first, fall back to localStorage
 */
export async function savePreset(name: string, filterRoot: FilterGroup): Promise<FilterPreset> {
  try {
    const preset = await savePresetToServer(name, filterRoot);
    setStorageMode(STORAGE_MODE.SERVER);
    return preset;
  } catch (error) {
    console.warn('Server save failed, falling back to localStorage:', error);

    // Fallback to localStorage
    const preset: FilterPreset = {
      id: generatePresetId(),
      name: name.trim(),
      createdAt: new Date().toISOString(),
      filterRoot: JSON.parse(JSON.stringify(filterRoot))
    };

    const presets = getPresetsFromLocalStorage();
    presets.push(preset);
    savePresetsToLocalStorage(presets);
    setStorageMode(STORAGE_MODE.LOCAL);

    return preset;
  }
}

/**
 * Load a preset by ID - try server first, fall back to localStorage
 */
export async function loadPreset(id: string): Promise<FilterGroup | null> {
  try {
    // Try to load from server
    const presets = await getPresetsFromServer();
    const preset = presets.find(p => p.id === id);
    setStorageMode(STORAGE_MODE.SERVER);

    if (preset) {
      return JSON.parse(JSON.stringify(preset.filterRoot));
    }

    // Not found on server, try localStorage
    return loadPresetFromLocalStorage(id);
  } catch (error) {
    console.warn('Server load failed, using localStorage:', error);
    setStorageMode(STORAGE_MODE.LOCAL);
    return loadPresetFromLocalStorage(id);
  }
}

/**
 * Delete a preset - try server first, fall back to localStorage
 */
export async function deletePreset(id: string): Promise<boolean> {
  try {
    const success = await deletePresetFromServer(id);
    if (success) {
      setStorageMode(STORAGE_MODE.SERVER);
    }
    return success;
  } catch (error) {
    console.warn('Server delete failed, trying localStorage:', error);

    // Fallback to localStorage
    const presets = getPresetsFromLocalStorage();
    const index = presets.findIndex(p => p.id === id);

    if (index === -1) {
      return false;
    }

    presets.splice(index, 1);
    savePresetsToLocalStorage(presets);
    setStorageMode(STORAGE_MODE.LOCAL);

    return true;
  }
}

/**
 * Rename a preset - try server first, fall back to localStorage
 */
export async function renamePreset(id: string, newName: string): Promise<boolean> {
  try {
    const success = await renamePresetOnServer(id, newName);
    if (success) {
      setStorageMode(STORAGE_MODE.SERVER);
    }
    return success;
  } catch (error) {
    console.warn('Server rename failed, trying localStorage:', error);

    // Fallback to localStorage
    const presets = getPresetsFromLocalStorage();
    const preset = presets.find(p => p.id === id);

    if (!preset) {
      return false;
    }

    preset.name = newName.trim();
    savePresetsToLocalStorage(presets);
    setStorageMode(STORAGE_MODE.LOCAL);

    return true;
  }
}

// ============================================
// Migration Functions
// ============================================

/**
 * Get local presets that don't exist on server
 */
export async function getLocalOnlyPresets(): Promise<FilterPreset[]> {
  const localPresets = getPresetsFromLocalStorage();

  try {
    const serverPresets = await getPresetsFromServer();
    const serverIds = new Set(serverPresets.map((p) => p.id));
    return localPresets.filter((p) => !serverIds.has(p.id));
  } catch {
    // If server is unavailable, all local presets are "local-only"
    return localPresets;
  }
}

/**
 * Migrate local presets to server
 */
export async function migrateLocalPresetsToServer(): Promise<{
  success: boolean;
  migrated: number;
  errors: string[];
}> {
  const localPresets = getPresetsFromLocalStorage();
  const results = {
    success: true,
    migrated: 0,
    errors: [] as string[]
  };

  for (const preset of localPresets) {
    try {
      // Validate depth before uploading
      if (validateFilterDepth(preset.filterRoot)) {
        await savePresetToServer(preset.name, preset.filterRoot);
        results.migrated++;
      } else {
        results.errors.push(`"${preset.name}" - Filterstruktur zu tief`);
      }
    } catch (error) {
      results.errors.push(`"${preset.name}" - ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
      results.success = false;
    }
  }

  if (results.success) {
    // Clear localStorage after successful migration
    localStorage.removeItem(STORAGE_KEYS.FILTER_PRESETS);
    // Set migration flag
    localStorage.setItem('chickpeace-migrated', 'true');
  }

  return results;
}

/**
 * Check if migration is needed
 */
export function needsMigration(): boolean {
  const hasLocalPresets = getPresetsFromLocalStorage().length > 0;
  const hasMigrated = localStorage.getItem('chickpeace-migrated') === 'true';
  return hasLocalPresets && !hasMigrated;
}

/**
 * Validate filter tree depth
 */
function validateFilterDepth(filterRoot: FilterGroup, depth: number = 0): boolean {
  if (depth > MAX_NESTING_DEPTH) {
    return false;
  }

  if (filterRoot.children) {
    for (const child of filterRoot.children) {
      if ('type' in child && child.type === 'group') {
        if (!validateFilterDepth(child as FilterGroup, depth + 1)) {
          return false;
        }
      }
    }
  }

  return true;
}
